import React from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../Common/hooks/useToast";
import { useLoginModule } from "./hooks/login_module.hook";
import style from "./login.module.css";

const OtpInputs: React.FC<{
  otp: string[];
  otpSent: boolean;
  timer: number;
  otpInputsRef: React.RefObject<(HTMLInputElement | null)[]>;
  onOtpChange: (value: string, index: number) => void;
  onOtpKeyDown: (e: React.KeyboardEvent<HTMLInputElement>, index: number) => void;
  onOtpPaste: (e: React.ClipboardEvent<HTMLInputElement>) => void;
  onResend: () => void;
}> = ({ otp, otpSent, timer, otpInputsRef, onOtpChange, onOtpKeyDown, onOtpPaste, onResend }) => (
  <div className={`${style.otpSection} ${otpSent ? style.visible : ""}`}>
    <div className={style.formGroup}>
      <label>Enter OTP</label>
      <div className={style.otpInputs}>
        {otp.map((digit, idx) => (
          <input
            key={idx}
            type="text"
            maxLength={1}
            inputMode="numeric"
            value={digit}
            ref={(el) => { if (otpInputsRef.current) otpInputsRef.current[idx] = el; }}
            onChange={(e) => onOtpChange(e.target.value, idx)}
            onKeyDown={(e) => onOtpKeyDown(e, idx)}
            onPaste={onOtpPaste}
          />
        ))}
      </div>
    </div>
    <div className={style.resendLink}>
      {timer > 0 ? (
        <span>Resend OTP in {timer}s</span>
      ) : (
        <span style={{ cursor: "pointer", color: "var(--primary)", fontWeight: 700 }} onClick={onResend}>
          Resend OTP
        </span>
      )}
    </div>
  </div>
);

export const DoctorLogin: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  React.useEffect(() => {
    const doctorId = localStorage.getItem("doctorId");
    const doctorRole = localStorage.getItem("doctorRole");
    if (doctorId && doctorRole === "DOCTOR") {
      navigate("/dashboard", { replace: true });
    }
  }, [navigate]);

  const {
    activeTab,
    phone,
    email,
    otpSent,
    otp,
    timer,
    isSending,
    errorMsg,
    otpInputsRef,
    setActiveTab,
    setPhone,
    setEmail,
    handleSendOtp,
    handleVerifyOtp,
    handleOtpChange,
    handleOtpKeyDown,
    handleOtpPaste,
  } = useLoginModule();

  return (
    <div className={style.loginBody}>
      {toast.show && (
        <div className={`${style.toastMsg} ${style.show} ${style[toast.type]}`}>
          <span className="material-icons-round">
            {toast.type === "success" ? "check_circle" : "error"}
          </span>
          {toast.msg}
        </div>
      )}

      <div className={style.loginContainer}>
        {/* Logo */}
        <div className={style.logoSection}>
          <img
            src="/logo-transparent3.png"
            alt="Pococare"
            onError={(e) => {
              (e.target as HTMLImageElement).src =
                "https://nice-stone-091e25e00.3.azurestaticapps.net/logo-transparent3.png";
            }}
          />
          <div className={style.logoSubtitle}>
            <span className={style.wordDoctor}>Doctor</span>{" "}
            <span className={style.wordPortal}>Portal</span>
          </div>
        </div>

        {/* Login Card */}
        <div className={style.loginCard}>
          <h2>Welcome back</h2>
          <p className={style.subtitle}>Sign in to manage your consultations</p>

          {/* Tab Switch */}
          <div className={style.tabSwitch}>
            <button
              className={activeTab === "phone" ? style.active : ""}
              onClick={() => setActiveTab("phone")}
            >
              Phone + OTP
            </button>
            <button
              className={activeTab === "email" ? style.active : ""}
              onClick={() => setActiveTab("email")}
            >
              Email
            </button>
          </div>

          {errorMsg && (
            <div style={{ color: "var(--error)", fontSize: "13px", fontWeight: 600, marginBottom: "12px" }}>
              {errorMsg}
            </div>
          )}

          {/* Phone Form */}
          {activeTab === "phone" && (
            <div>
              <div className={style.formGroup}>
                <label>Phone Number</label>
                <div className={style.inputWrapper}>
                  <span className={style.prefix}>+91</span>
                  <input
                    type="tel"
                    className={style.withPrefix}
                    placeholder="Enter your phone number"
                    maxLength={10}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                    disabled={otpSent}
                  />
                </div>
              </div>
              <OtpInputs
                otp={otp}
                otpSent={otpSent}
                timer={timer}
                otpInputsRef={otpInputsRef}
                onOtpChange={handleOtpChange}
                onOtpKeyDown={handleOtpKeyDown}
                onOtpPaste={handleOtpPaste}
                onResend={handleSendOtp}
              />
              <button className={style.btnPrimary} disabled={isSending} onClick={otpSent ? handleVerifyOtp : handleSendOtp}>
                {isSending ? "Processing..." : otpSent ? "Verify & Login" : "Send OTP"}
              </button>
            </div>
          )}

          {/* Email Form */}
          {activeTab === "email" && (
            <div>
              <div className={style.formGroup}>
                <label>Email Address</label>
                <input
                  type="email"
                  placeholder="doctor@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={otpSent}
                />
              </div>
              <OtpInputs
                otp={otp}
                otpSent={otpSent}
                timer={timer}
                otpInputsRef={otpInputsRef}
                onOtpChange={handleOtpChange}
                onOtpKeyDown={handleOtpKeyDown}
                onOtpPaste={handleOtpPaste}
                onResend={handleSendOtp}
              />
              <button className={style.btnPrimary} disabled={isSending} onClick={otpSent ? handleVerifyOtp : handleSendOtp}>
                {isSending ? "Processing..." : otpSent ? "Verify & Login" : "Send OTP"}
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={style.footerText}>
          By signing in, you agree to our
          <br />
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a href="#" onClick={(e) => e.preventDefault()}>Terms of Service</a> &{" "}
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a href="#" onClick={(e) => e.preventDefault()}>Privacy Policy</a>
        </div>
      </div>
    </div>
  );
};
